// The world beyond the square: three more districts of town, the Town Gate, and the first open-world area.
// Town zones are cozy (no mounts); open-world zones allow mounts and, where set, flying.
import { ARENAS, type ArenaPlace } from './arenas.js';
import type { ZoneMapData } from './map.js';
import { T, ZoneBuilder, rng, type HouseDef, type ZoneArt } from './zonebuild.js';
import { OTHER_HOMELANDS } from './homelands.js';
import { GARDEN_ZONE } from './garden.js';
import { MEADOW_SPOTS, WOODS_SPOTS } from './spots.js';

export type ZoneKind = 'town' | 'field' | 'world' | 'arena';   // world: the overworld between places; arena: a battle
/** Where a creature lives (tiles). */
export interface MobSpawn { kind: string; x: number; y: number }
export interface ZoneDef { id: string; name: string; kind: ZoneKind; mounts: boolean; flying: boolean; map: ZoneMapData; art: ZoneArt | null; wayshrine: [number, number] | null; mobs: MobSpawn[] }

const H = (w: number, h: number, roof: string, roofD: string, door: number, icon: string, name: string): HouseDef => ({ w, h, roof, roofD, door, icon, name });
const map = (id: string, name: string, b: ZoneBuilder, spawn: [number, number]) => { const f = b.finish(); return { data: { id, name, width: b.w, height: b.h, tileSize: 16, spawn, collision: f.collision } as ZoneMapData, art: f.art }; };

/** Night creatures: a few more spawn points, used only after dark (the server hides night creatures by day). */
function nightSpawns(b: ZoneBuilder, mobs: MobSpawn[], kind: string, n: number, seed: number, ok: (x: number, y: number) => boolean): void {
  const r = rng(seed);
  for (let tries = 0; tries < 3000 && n > 0; tries++) {
    const x = 3 + Math.floor(r() * (b.w - 6)), y = 3 + Math.floor(r() * (b.h - 6));
    if (!b.free(x, y) || !ok(x, y) || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 4)) continue;
    mobs.push({ kind, x, y }); n--;
  }
}

// ---------- Harbor: west of the square ----------
function harbor() {
  const b = new ZoneBuilder(36, 24);
  b.fill(0, 15, 35, 23, T.WATER).fill(0, 12, 35, 14, T.SAND).fill(2, 10, 35, 11, T.STONE).fill(34, 11, 35, 13, T.PATH);
  b.fill(8, 15, 9, 21, T.PIER).fill(20, 15, 21, 20, T.PIER).fill(28, 15, 29, 18, T.PIER);
  b.border([[34, 10, 35, 13], [0, 12, 35, 23]]);
  for (let y = 12; y < 24; y++) b.unblock(0, y).unblock(35, y);
  for (let x = 0; x < 36; x++) b.unblock(x, 23);
  b.block(34, 14, 35, 23);   // the sea is the edge
  b.house(3, 3, H(6, 5, '#4e83ad', '#3d6a8f', 2, 'shield', 'Harbor Office')).doorPath(3, 3, H(6, 5, '', '', 2, '', ''), 9);
  b.house(12, 2, H(7, 6, '#7a8a6a', '#5a6a4e', 3, 'bread', 'Fish Market')).doorPath(12, 2, H(7, 6, '', '', 3, '', ''), 9);
  b.house(24, 3, H(6, 5, '#b46a4a', '#8c4a3a', 2, '', 'Warehouse')).doorPath(24, 3, H(6, 5, '', '', 2, '', ''), 9);
  for (const x of [4, 11, 17, 23, 30]) b.lamp(x, 9);
  b.stall(14, 13).stall(25, 13);
  b.put('barrel', 6, 12).put('crate', 7, 12).put('crate', 10, 11).put('barrel', 30, 14).put('crate', 31, 14).put('barrel', 19, 9);
  b.put('boat', 11, 17, 0, false).put('boat', 23, 18, 1, false).put('boat', 31, 19, 0, false);
  b.bench(26, 9);
  b.put('wayshrine', 18, 7);
  b.scatter(11, 16, ['tuft', 'bush', 'mushroom'], [2, 2, 33, 8]);
  return { ...map('harbor', 'Harbor', b, [32, 12]) };
}

// ---------- Artisan Row: north of the square ----------
function artisan() {
  const b = new ZoneBuilder(34, 22);
  b.fill(16, 8, 17, 21, T.PATH).fill(3, 9, 30, 10, T.PATH).fill(12, 11, 21, 16, T.STONE);
  b.border([[16, 20, 17, 21]]);
  const smith = H(7, 5, '#5a5e6b', '#3f424c', 3, 'shield', 'Smithy'), wood = H(6, 5, '#b07a4a', '#8a5a34', 2, '', 'Carpenter'), weave = H(6, 5, '#a77daa', '#7d5a80', 2, 'flower', 'Weaver');
  b.house(3, 3, smith).doorPath(3, 3, smith, 9); b.house(14, 2, wood).doorPath(14, 2, wood, 9); b.house(24, 3, weave).doorPath(24, 3, weave, 9);
  b.put('anvil', 8, 11).put('barrel', 3, 11).put('crate', 4, 11).put('crate', 27, 11).put('pot', 30, 8, 1).put('pot', 22, 8, 0);
  b.put('well', 16, 13);
  for (const x of [7, 13, 20, 26]) b.lamp(x, 8);
  b.bench(13, 17).bench(19, 17);
  b.put('wayshrine', 20, 13);
  b.scatter(21, 26, ['tuft', 'bush', 'mushroom', 'rock'], [2, 12, 31, 19]);
  return map('artisan', 'Artisan Row', b, [16, 18]);
}

// ---------- Cottage Lane: east of the square, the housing district ----------
function cottages() {
  const b = new ZoneBuilder(40, 22);
  b.fill(0, 10, 39, 11, T.PATH);
  b.border([[0, 10, 1, 11]]);
  const roofs: [string, string][] = [['#db614f', '#b4473b'], ['#4e83ad', '#3d6a8f'], ['#d67599', '#a85a7a'], ['#8fbf5a', '#5e8a3a'], ['#e0a94e', '#b07a2e']];
  for (let i = 0; i < 5; i++) {
    const x = 4 + i * 7, top = H(5, 4, roofs[i][0], roofs[i][1], 2, i % 2 ? 'flower' : '', 'Cottage'), bot = H(5, 4, roofs[(i + 2) % 5][0], roofs[(i + 2) % 5][1], 2, '', 'Cottage');
    b.house(x, 4, top).doorPath(x, 4, top, 9);
    b.house(x, 14, bot);   // the far row faces away from the lane: neighbours' homes, for now
    b.put('pot', x - 1, 8, i % 3).put('pot', x + 5, 8, (i + 1) % 3);
  }
  b.put('lamp', 8, 12).put('lamp', 22, 12).put('lamp', 36, 12).put('lamp', 15, 9).put('lamp', 29, 9);
  b.put('wayshrine', 3, 13);
  b.scatter(31, 30, ['tuft', 'bush', 'mushroom'], [2, 2, 37, 19]);
  return map('cottages', 'Cottage Lane', b, [2, 11]);
}
/** Every cottage door in Cottage Lane leads home: to your own cottage. */
export const COTTAGE_DOORS: [number, number][] = [];
for (let i = 0; i < 5; i++) COTTAGE_DOORS.push([4 + i * 7 + 2, 7]);

// ---------- the Town Gate: south of the square ----------
function gate() {
  const b = new ZoneBuilder(30, 20);
  b.fill(14, 0, 15, 19, T.PATH).fill(10, 8, 19, 11, T.STONE);
  b.border([[14, 0, 15, 1], [14, 18, 15, 19]]);
  for (let x = 2; x < 28; x++) if (x < 12 || x > 17) b.put('fence_h', x, 9);   // the town wall, with the gate open in the middle
  b.put('arch', 14, 9, 0, false);
  b.lamp(12, 8).lamp(17, 8).lamp(12, 12).lamp(17, 12);
  b.put('crate', 6, 6).put('barrel', 7, 6).put('barrel', 22, 13).put('sign', 17, 15);
  b.put('wayshrine', 9, 5);
  b.scatter(41, 34, ['tuft', 'bush', 'mushroom', 'rock'], [2, 2, 27, 17]);
  return map('gate', 'Town Gate', b, [14, 2]);
}

// ---------- the Meadowlands: the first open-world area ----------
/** Every tile you can walk to from a spot (for keeping creatures and gathering spots where you can reach them). */
function reach(b: ZoneBuilder, from: [number, number]): Set<number> {
  const seen = new Set<number>(), q: [number, number][] = [from];
  while (q.length) { const [x, y] = q.pop()!, k = y * b.w + x; if (seen.has(k) || !b.free(x, y)) continue; seen.add(k); q.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]); }
  return seen;
}
/** Take everything off some tiles (props and walls), for a road laid over land that's already been decorated. */
function clearTiles(b: ZoneBuilder, x0: number, y0: number, x1: number, y1: number): void {
  for (let i = b.props.length - 1; i >= 0; i--) { const p = b.props[i]; if (p.kind !== 'house' && p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1) b.props.splice(i, 1); }
  b.unblock(x0, y0, x1, y1);
}
function meadow() {
  // 96 by 64: the first 64 by 44 is the original Meadowlands, untouched; the land to the east and south is new
  // (an orchard, a timber grove, a fishing lake, a copper quarry, flower and herb meadows, more farm fields).
  const W0 = 64, H0 = 44, b = new ZoneBuilder(96, 64);
  // a river from the northeast to the southwest, with a bridge on the road
  for (let y = 0; y < b.h; y++) { const cx = 44 - y * 0.55 + Math.sin(y * 0.35) * 2.5; b.fill(Math.round(cx - 1.5), y, Math.round(cx + 1.5), y, T.WATER); }
  b.fill(30, 0, 31, 30, T.PATH).fill(8, 29, 56, 30, T.PATH);
  for (let y = 0; y < H0; y++) for (let x = 0; x < W0; x++) if (b.at(x, y) === T.WATER && (x === 30 || x === 31) && y < 31) b.fill(x, y, x, y, T.PIER);
  for (let y = 29; y <= 30; y++) for (let x = 0; x < W0; x++) if (b.at(x, y) === T.WATER) b.fill(x, y, x, y, T.PIER);
  b.ellipse(12, 12, 6, 4, T.WATER).ellipse(12, 12, 7.5, 5.5, T.SAND); b.ellipse(12, 12, 6, 4, T.WATER);
  b.fill(46, 34, 56, 40, T.SOIL);
  // the new land's ground first (the old land's trees and scatter below don't reach it)
  b.ellipse(80, 40, 9, 6.5, T.SAND).ellipse(80, 40, 7.5, 5, T.WATER);          // the fishing lake
  b.fill(80, 32, 80, 38, T.PIER);                                                 // its pier, from the north shore
  for (let y = 44; y < 62; y++) for (let x = 83; x < 95; x++) if (Math.hypot((x - 89) / 7, (y - 53) / 9) < 1) b.fill(x, y, x, y, T.ROCK);   // the copper quarry
  b.fill(50, 46, 60, 51, T.SOIL);                                                  // more farm fields, south of the farm
  b.border([[30, 0, 31, 1]]);
  // woods in the west and south, scattered trees elsewhere
  const r = (s: number) => { let x = s; return () => { x = (x * 1103515245 + 12345) & 0x7fffffff; return x / 0x7fffffff; }; };
  const rr = r(7);
  for (let i = 0; i < 260; i++) {
    const x = 2 + Math.floor(rr() * 60), y = 2 + Math.floor(rr() * 40);
    const woods = (x < 20 && y > 20) || (y > 36 && x < 40) || (x > 52 && y < 18);
    if ((woods ? rr() < 0.75 : rr() < 0.12) && b.at(x, y) === T.GRASS && !b.blocked(x, y) && Math.abs(x - 30.5) > 2 && Math.abs(y - 29.5) > 2) b.tree(x, y);
  }
  b.house(44, 31, H(6, 3, '#b07a4a', '#8a5a34', 2, '', 'Farmhouse'));
  b.fenceH(45, 57, 33, [50]).fenceH(45, 57, 41);
  b.put('reeds', 7, 9, 0, false).put('reeds', 17, 14, 0, false).put('reeds', 9, 16, 0, false);
  b.put('sign', 33, 3).put('wayshrine', 33, 6);
  b.scatter(51, 140, ['tuft', 'tuft', 'bush', 'mushroom', 'rock'], [2, 2, 61, 41]);
  // ---------- the new land ----------
  // roads: the east road on to the lake and the grove, and a lane south to the meadows and the quarry
  b.fill(57, 29, 92, 30, T.PATH); clearTiles(b, 57, 29, 62, 30);
  b.fill(70, 31, 71, 58, T.PATH).fill(34, 52, 69, 53, T.PATH).fill(72, 56, 86, 57, T.PATH).fill(88, 3, 88, 28, T.PATH);   // (and a woodcutters' track up through the grove)
  for (let y = 0; y < b.h; y++) for (let x = 0; x < b.w; x++) if (b.at(x, y) === T.WATER && (y === 52 || y === 53) && x >= 34) b.fill(x, y, x, y, T.PIER);   // a footbridge where the lane crosses the river
  const sp = MEADOW_SPOTS, near = (x: number, y: number, list: [number, number][], d = 1) => list.some(([a, c]) => Math.abs(a - x) <= d && Math.abs(c - y) <= d);
  const all = [...sp.orchard, ...sp.grove, ...sp.lake, ...sp.quarry, ...sp.herbs];
  const r3 = rng(9601);
  // the orchard: rows of blossom trees, the picking spots between them
  for (let y = 3; y <= 15; y += 4) for (let x = 64; x <= 78; x += 2) if (!near(x, y, sp.orchard, 0) && b.free(x, y)) b.tree(x, y, 'blossom');
  // the timber grove: thick oaks and pines in the north-east, with felled logs by the cutting spots
  for (let y = 2; y <= 27; y++) for (let x = 82; x <= 93; x++) if (b.free(x, y) && b.at(x, y) === T.GRASS && !near(x, y, sp.grove) && Math.abs(y - 29.5) > 2 && r3() < 0.42) b.tree(x, y, r3() < 0.5 ? 'pine' : 'oak');
  for (const [x, y] of sp.grove) if (b.free(x + 1, y) && !near(x + 1, y, all, 0)) b.put('log', x + 1, y);
  // the quarry: boulders round the copper
  for (let y = 44; y < 62; y++) for (let x = 83; x < 95; x++) if (b.at(x, y) === T.ROCK && b.free(x, y) && !near(x, y, sp.quarry) && !(y === 56 || y === 57) && r3() < 0.22) b.put('rock', x, y, Math.floor(r3() * 3));
  // the lake: reeds and lilies, and a boat at the pier
  b.put('reeds', 73, 37, 0, false).put('reeds', 87, 43, 0, false).put('lily', 77, 42, 0, false).put('lily', 83, 38, 0, false).put('boat', 82, 37, 0, false);
  // the new fields and their crops; flowers and herbs in the south meadows; a few trees here and there
  for (let x = 51; x <= 59; x += 2) for (let y = 47; y <= 50; y++) b.put(y % 2 ? 'cabbage' : 'carrot', x, y, 0, false);
  b.fenceH(49, 61, 45, [55]);
  for (let i = 0; i < 420; i++) {
    const x = 2 + Math.floor(r3() * 92), y = 2 + Math.floor(r3() * 60);
    if (x < W0 && y < H0) continue;                                              // the old land is as it was
    if (b.at(x, y) !== T.GRASS || !b.free(x, y) || near(x, y, all, 1) || [29, 30, 52, 53, 56, 57].some(v => Math.abs(v - y) < 1) || Math.abs(x - 70.5) < 2) continue;
    const south = y > 44 && x < 80, k = r3();
    if (south && k < 0.12) b.tree(x, y, 'oak');
    else if (!south && k < 0.08) b.tree(x, y);
  }
  b.scatter(9602, 220, ['tuft', 'tuft', 'tuft', 'bush', 'mushroom', 'rock'], [62, 2, 93, 61]);
  b.scatter(9603, 220, ['tuft', 'tuft', 'tuft', 'bush', 'mushroom'], [2, 42, 62, 61]);
  for (const [x, y] of all) clearTiles(b, x, y, x, y);                           // nothing grows on a gathering spot
  // creatures: mosslings by the water, moonhares in the open grass, pebblecrabs at the edge of the woods.
  // None live near the road in from town, so arriving is always calm.
  const mobs: MobSpawn[] = [], r2 = rng(77), want: Record<string, number> = { puff: 14, bunny: 12, beetle: 10 };
  for (let tries = 0; tries < 4000 && Object.values(want).some(n => n > 0); tries++) {
    const x = 3 + Math.floor(r2() * 58), y = 3 + Math.floor(r2() * 38);
    if (!b.free(x, y) || b.at(x, y) !== T.GRASS || (y < 12 && Math.abs(x - 30.5) < 9) || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 4)) continue;
    let water = false; for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) if (b.at(x + dx, y + dy) === T.WATER) water = true;
    const woods = (x < 22 && y > 18) || (y > 34 && x < 42) || (x > 50 && y < 20);
    const kind = water && want.puff > 0 ? 'puff' : woods && want.beetle > 0 ? 'beetle' : !woods && want.bunny > 0 ? 'bunny' : want.puff > 0 && !woods ? 'puff' : '';
    if (!kind) continue;
    want[kind]--; mobs.push({ kind, x, y });
  }
  // and in the new land: mosslings round the lake, moonhares in the orchard and meadows, pebblecrabs in the grove and quarry
  const want2: Record<string, number> = { puff: 8, bunny: 10, beetle: 8 }, r4 = rng(9604);
  for (let tries = 0; tries < 6000 && Object.values(want2).some(n => n > 0); tries++) {
    const x = 3 + Math.floor(r4() * 90), y = 3 + Math.floor(r4() * 58);
    if (x < W0 - 1 && y < H0 - 1) continue;
    if (!b.free(x, y) || (b.at(x, y) !== T.GRASS && b.at(x, y) !== T.ROCK) || near(x, y, all, 1) || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 4)) continue;
    const lake = Math.hypot((x - 80) / 11, (y - 40) / 8.5) < 1, rough = b.at(x, y) === T.ROCK || (x > 81 && y < 28);
    const kind = lake && want2.puff > 0 ? 'puff' : rough && want2.beetle > 0 ? 'beetle' : !rough && !lake && want2.bunny > 0 ? 'bunny' : '';
    if (!kind) continue;
    want2[kind]--; mobs.push({ kind, x, y });
  }
  nightSpawns(b, mobs, 'dreamhare', 7, 31, (x, y) => b.at(x, y) === T.GRASS && !(y < 12 && Math.abs(x - 30.5) < 9) && x < W0 - 3 && y < H0 - 3);
  nightSpawns(b, mobs, 'dreamhare', 4, 32, (x, y) => b.at(x, y) === T.GRASS && (x >= W0 || y >= H0));
  { const ok = reach(b, [30, 3]); for (let i = mobs.length - 1; i >= 0; i--) if ((mobs[i].x >= W0 - 1 || mobs[i].y >= H0 - 1) && !ok.has(mobs[i].y * b.w + mobs[i].x)) mobs.splice(i, 1); }   // nothing out of reach in the new land
  const out = map('meadow', 'Meadowlands', b, [30, 3]);
  out.data.mounts = true; out.data.flying = true;
  return { ...out, mobs };
}

// ---------- the overworld: a small SNES-style world map between places ----------
/** Places on the overworld: walk onto one to go in. Locked ones are for later (world bosses, the airship). */
export interface Place { id: string; name: string; tile: [number, number]; zone: string | null; at: [number, number]; levels: string; blurb: string; locked?: string; /** which overworld it's on (default: Sunvale's, 'world') */ world?: string;
  /** A dangerous place: you need this allegiance rank at home (points, shared/nations.ts RANKS) and job level to go in. */
  need?: { rank: number; level: number } }
export const PLACES: Place[] = [
  { id: 'town', name: 'Tiny Town', tile: [40, 27], zone: 'gate', at: [14, 17], levels: 'Safe', blurb: 'Home: the square, the harbor, Artisan Row and Cottage Lane.' },
  { id: 'meadow', name: 'Meadowlands', tile: [41, 37], zone: 'meadow', at: [30, 2], levels: 'Lv 1–3', blurb: 'Mosslings, moonhares and pebblecrabs. A gentle start.' },
  { id: 'woods', name: 'Whisperwood', tile: [23, 27], zone: 'woods', at: [57, 22], levels: 'Lv 4–8', blurb: 'Old trees, lanterncaps, imps and thorn rollers. Something big roams the deep woods.' },
  { id: 'grave', name: 'Gloomhollow', tile: [17, 12], zone: 'grave', at: [24, 37], levels: 'Lv 6–11', blurb: 'An old graveyard. Teacup haunts guard the graves, and inkveils drift out at night.' },
  { id: 'peak', name: "Dragon's Spine", tile: [62, 8], zone: 'spine', at: [20, 26], levels: 'World boss', blurb: 'The Star-Eater sleeps on the summit, ringed by mountains: only an airship gets you there. It wakes every two hours. Bring an alliance.' },
  { id: 'crags', name: 'Rustwind Crags', tile: [42, 14], zone: 'crags', at: [28, 37], levels: 'Lv 10–17', blurb: 'Windy high country under the northern ridge. Cinderbats and clockwork wasps come in packs: bring a party.', need: { rank: 0, level: 10 } },
  { id: 'dock', name: 'Sky Dock', tile: [65, 33], zone: null, at: [0, 0], levels: 'Airships', blurb: 'A pier for airships. Build one here (your party can chip in), then board it and fly over the mountains.' },
];
export const placeOf = (zone: string) => PLACES.find(p => p.zone === zone);

function overworld() {
  const W = 80, H = 52, b = new ZoneBuilder(W, H, T.WATER), r = rng(2026);
  // the continent: a few overlapping blobs, a little ragged at the edge
  const blobs: [number, number, number, number][] = [[40, 27, 30, 19], [22, 16, 12, 9], [62, 14, 13, 9], [58, 34, 11, 9], [30, 40, 13, 7]];
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const n = Math.sin(x * 0.7 + y * 0.3) * 0.06 + Math.sin(y * 0.9 - x * 0.4) * 0.05;
    if (blobs.some(([cx, cy, rx, ry]) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1 + n)) b.fill(x, y, x, y, T.GRASS);
  }
  // beaches
  for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) if (b.at(x, y) === T.GRASS && [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => b.at(x + dx, y + dy) === T.WATER)) b.fill(x, y, x, y, T.SAND);
  // mountains: the Dragon's Spine in the north-east, and a ridge in the north
  const ridge = (cx: number, cy: number, rx: number, ry: number) => { for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (b.at(x, y) === T.GRASS && ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1) b.fill(x, y, x, y, T.ROCK); };
  ridge(61, 10, 11, 6); ridge(34, 9, 8, 3.5);
  // forests: Whisperwood around its clearing, and a few smaller woods
  const wood = (cx: number, cy: number, rx: number, ry: number) => { for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (b.at(x, y) === T.GRASS && ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1) b.fill(x, y, x, y, T.FOREST); };
  wood(22, 27, 9, 7); wood(52, 23, 5, 4); wood(14, 18, 5, 4); wood(55, 40, 4, 3);
  // a river from the northern ridge down to the sea in the south-west
  for (let y = 11; y < H; y++) { const cx = 34 - (y - 11) * 0.45 + Math.sin(y * 0.4) * 1.6; for (let x = Math.round(cx); x <= Math.round(cx) + 1; x++) if (b.at(x, y) !== T.ROCK && b.at(x, y) !== -1) b.fill(x, y, x, y, T.WATER); }
  // roads (bridges where they cross water)
  const road = (pts: [number, number][]) => {
    for (let i = 0; i + 1 < pts.length; i++) {
      let [x, y] = pts[i]; const [tx, ty] = pts[i + 1];
      while (x !== tx || y !== ty) {
        const t = b.at(x, y); if (t !== -1 && t !== T.ROCK) b.fill(x, y, x, y, t === T.WATER ? T.PIER : T.PATH);
        if (Math.abs(tx - x) >= Math.abs(ty - y)) x += Math.sign(tx - x); else y += Math.sign(ty - y);
      }
      const t = b.at(tx, ty); if (t !== -1 && t !== T.ROCK) b.fill(tx, ty, tx, ty, t === T.WATER ? T.PIER : T.PATH);
    }
  };
  road([[40, 28], [40, 33], [41, 36]]);                         // town to the Meadowlands
  road([[39, 28], [33, 28], [24, 28]]);                         // west to Whisperwood (over the river)
  road([[33, 28], [28, 22], [21, 16], [17, 13]]);               // north-west to Gloomhollow
  road([[41, 28], [52, 30], [64, 33]]);                         // east to the Sky Dock
  road([[41, 26], [41, 20], [42, 15]]);                         // north to the Rustwind Crags
  // trees in the woods (walkable little groves), peaks on the mountains (only an airship gets over)
  for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
    const t = b.at(x, y);
    if (t === T.FOREST && r() < 0.55 && !PLACES.some(p => Math.abs(p.tile[0] - x) <= 1 && Math.abs(p.tile[1] - y) <= 1)) b.put('grove', x, y, Math.floor(r() * 3), false);
    if (t === T.ROCK && !PLACES.some(p => Math.abs(p.tile[0] - x) <= 1 && Math.abs(p.tile[1] - y) <= 1)) b.put('peak', x, y, Math.floor(r() * 3)).mountain(x, y);
  }
  // the sea is the edge of the world (nobody leaves it, not even airships)
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (x === 0 || y === 0 || x === W - 1 || y === H - 1) b.block(x, y);
  // the places, and a sign by each locked one
  PLACES.forEach((p, i) => { b.put('loc', p.tile[0], p.tile[1], i, false); b.fill(p.tile[0], p.tile[1], p.tile[0], p.tile[1], p.locked ? T.ROCK : T.PATH); });
  b.unblock(62, 8).unblock(65, 33);
  b.scatter(5, 50, ['tuft', 'bush', 'rock'], [2, 2, W - 3, H - 3], []);
  const out = map('world', 'The World', b, [40, 29]);
  out.data.mounts = true; out.data.flying = true; out.data.airship = true; out.data.walk = 55;
  // wandering shadows: touch one (or let it catch you) and the creatures of that region attack
  const mobs: MobSpawn[] = [], r3 = rng(4242);
  for (let tries = 0; mobs.length < 18 && tries < 4000; tries++) {
    const x = 2 + Math.floor(r3() * (W - 4)), y = 2 + Math.floor(r3() * (H - 4));
    if (b.at(x, y) !== T.GRASS && b.at(x, y) !== T.FOREST) continue;
    if (PLACES.some(p => Math.abs(p.tile[0] - x) <= 4 && Math.abs(p.tile[1] - y) <= 4)) continue;
    let road = false; for (let dy = -2; dy <= 2 && !road; dy++) for (let dx = -2; dx <= 2; dx++) if (b.at(x + dx, y + dy) === T.PATH) { road = true; break; }
    if (road || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 6)) continue;
    mobs.push({ kind: 'shadow', x, y });
  }
  return { ...out, mobs };
}

// ---------- Whisperwood: the forest west of town ----------
function woods() {
  // 60 by 66: the first 44 rows are the original Whisperwood, untouched; below them, a new southern hollow
  // (a woodcutters' camp in a clearing, mushroom rings, ferns, a still pond), reached by a path south.
  const W = 60, H0 = 44, H = 66, b = new ZoneBuilder(W, H, T.FOREST);
  // a winding path in from the east, a stream, mushroom glades
  const pathY = (x: number) => x > W - 12 ? 22 : Math.round(22 + Math.sin(x * 0.18) * 4 * Math.min(1, (W - 12 - x) / 8));
  for (let x = 6; x < W; x++) { const y = pathY(x); b.fill(x, y, x, y + 1, T.PATH); }
  for (let y = 0; y < H; y++) { const cx = 20 + Math.sin(y * 0.3) * 3; b.fill(Math.round(cx), y, Math.round(cx) + 1, y, T.WATER); }
  for (let x = 0; x < W; x++) for (let y = 0; y < H; y++) if (b.at(x, y) === T.WATER && Math.abs(y - pathY(x)) <= 1) b.fill(x, y, x, y, T.PIER);
  b.ellipse(36, 10, 6, 4, T.GRASS).ellipse(10, 34, 6, 5, T.GRASS).ellipse(44, 34, 7, 5, T.GRASS);
  // the southern hollow's ground
  b.ellipse(30, 54, 7, 5, T.GRASS).ellipse(47, 56, 5, 3.5, T.SAND).ellipse(47, 56, 4, 2.6, T.WATER).ellipse(12, 56, 5, 4, T.GRASS);
  b.border([[W - 1, 20, W - 1, 28]]);
  const r = rng(404);
  for (let i = 0; i < 520; i++) {
    const x = 2 + Math.floor(r() * (W - 4)), y = 2 + Math.floor(r() * (H0 - 4)), here = b.at(x, y);
    if (here !== T.FOREST || b.blocked(x, y)) continue;
    const pathNear = [-2, -1, 0, 1, 2].some(d => b.at(x, y + d) === T.PATH);
    if (!pathNear && r() < 0.55) b.tree(x, y, r() < 0.45 ? 'pine' : 'oak');
  }
  for (const [x, y] of [[34, 9], [38, 11], [9, 33], [12, 36], [42, 33], [47, 36]] as [number, number][]) b.put('bigshroom', x, y);
  for (const [x, y] of [[30, 26], [50, 18], [14, 14], [26, 38]] as [number, number][]) b.put('log', x, y);
  b.put('wayshrine', 54, 19).put('sign', 56, 25);
  b.scatter(12, 120, ['tuft', 'mushroom', 'bush', 'rock', 'tuft'], [2, 2, W - 3, H0 - 3]);
  // ---------- the southern hollow ----------
  const sy = pathY(40) + 2;
  b.fill(40, sy, 41, 59, T.PATH).fill(6, 54, 39, 55, T.PATH).fill(40, 59, 56, 60, T.PATH); clearTiles(b, 39, sy, 42, H0 - 1);
  for (let x = 0; x < W; x++) for (const y of [54, 55]) if (b.at(x, y) === T.WATER) b.fill(x, y, x, y, T.PIER);   // a plank bridge over the stream
  const sp = WOODS_SPOTS, all = [...sp.camp, ...sp.shrooms, ...sp.ferns, ...sp.pond], near = (x: number, y: number, d = 1) => all.some(([a, c]) => Math.abs(a - x) <= d && Math.abs(c - y) <= d);
  const r3 = rng(4405);
  for (let y = H0 - 2; y < H - 2; y++) for (let x = 2; x < W - 2; x++) {
    if (b.at(x, y) !== T.FOREST || !b.free(x, y) || near(x, y) || [-1, 0, 1].some(d => b.at(x + d, y) === T.PATH || b.at(x, y + d) === T.PATH)) continue;
    if (r3() < 0.4) b.tree(x, y, r3() < 0.5 ? 'pine' : 'oak');
  }
  for (const [x, y] of [[24, 51], [34, 57], [29, 49]] as [number, number][]) if (b.free(x, y) && !near(x, y, 0)) b.put('log', x, y);   // the woodcutters' felled trunks
  for (const [x, y] of [[13, 52], [8, 58], [52, 47]] as [number, number][]) if (b.free(x, y) && !near(x, y, 0)) b.put('bigshroom', x, y);
  b.put('reeds', 43, 57, 0, false).put('lily', 48, 56, 0, false).put('sign', 42, 46);
  b.scatter(4406, 90, ['tuft', 'mushroom', 'tuft', 'bush'], [2, H0 - 2, W - 3, H - 3]);
  for (const [x, y] of all) clearTiles(b, x, y, x, y);
  const mobs: MobSpawn[] = [], r2 = rng(99), want: Record<string, number> = { shroom: 10, wolf: 9, boar: 7, bramblehorn: 1 };
  for (let tries = 0; tries < 6000 && Object.values(want).some(n => n > 0); tries++) {
    const x = 3 + Math.floor(r2() * (W - 6)), y = 3 + Math.floor(r2() * (H0 - 6));
    if (!b.free(x, y) || x > W - 10 || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 4)) continue;
    const glade = b.at(x, y) === T.GRASS, deep = x < 18;
    const kind = want.bramblehorn > 0 && deep && y > 30 ? 'bramblehorn' : glade && want.shroom > 0 ? 'shroom' : deep && want.boar > 0 ? 'boar' : want.wolf > 0 ? 'wolf' : want.shroom > 0 && !deep ? 'shroom' : '';
    if (!kind) continue;
    want[kind]--; mobs.push({ kind, x, y });
  }
  // the hollow's own: lanterncaps in the clearings, imps and thorn rollers in the trees
  const want2: Record<string, number> = { shroom: 5, wolf: 5, boar: 4 }, r4 = rng(4407);
  for (let tries = 0; tries < 6000 && Object.values(want2).some(n => n > 0); tries++) {
    const x = 3 + Math.floor(r4() * (W - 6)), y = H0 + Math.floor(r4() * (H - H0 - 3));
    if (!b.free(x, y) || near(x, y) || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 4)) continue;
    const glade = b.at(x, y) === T.GRASS;
    const kind = glade && want2.shroom > 0 ? 'shroom' : !glade && want2.wolf > 0 ? 'wolf' : !glade && want2.boar > 0 ? 'boar' : '';
    if (!kind) continue;
    want2[kind]--; mobs.push({ kind, x, y });
  }
  nightSpawns(b, mobs, 'nightimp', 6, 41, (x, y) => x < W - 10 && y < H0 - 3);
  nightSpawns(b, mobs, 'nightimp', 2, 42, (x, y) => y >= H0);
  { const ok = reach(b, [57, 22]); for (let i = mobs.length - 1; i >= 0; i--) if (mobs[i].y >= H0 - 2 && !ok.has(mobs[i].y * b.w + mobs[i].x)) mobs.splice(i, 1); }   // (the old wood's creatures are as they were)
  const out = map('woods', 'Whisperwood', b, [57, 22]);
  out.data.mounts = true; out.data.flying = false;   // the canopy is too thick to fly
  return { ...out, mobs };
}

// ---------- Gloomhollow: the old graveyard ----------
function graveyard() {
  const GW = 50, GH = 40, b = new ZoneBuilder(GW, GH, T.GRASS);
  b.fill(23, 20, 24, GH - 1, T.PATH).fill(8, 19, 42, 20, T.PATH).fill(22, 6, 25, 19, T.PATH);
  b.border([[23, GH - 1, 24, GH - 1]]);
  b.house(19, 2, H(10, 5, '#5a5e6b', '#43464f', 5, '', 'Old Chapel'));
  // rows of graves with iron fences, a few dead trees
  for (const [gx, gy] of [[5, 8], [32, 8], [5, 24], [32, 24]] as [number, number][]) {
    for (let x = gx; x < gx + 13; x++) { if (x !== gx + 6) b.put('ironfence', x, gy - 1); b.put('ironfence', x, gy + 9); }
    for (let row = 0; row < 3; row++) for (let col = 0; col < 4; col++) b.put('tomb', gx + 1 + col * 3, gy + 1 + row * 3, (row + col) % 3);
  }
  const r = rng(13);
  for (let i = 0; i < 26; i++) { const x = 2 + Math.floor(r() * (GW - 4)), y = 2 + Math.floor(r() * (GH - 4)); if (b.at(x, y) === T.GRASS && !b.blocked(x, y)) b.put('deadtree', x, y); }
  b.put('candle', 21, 8, 0, false).put('candle', 27, 8, 0, false).put('wayshrine', 26, 33).put('sign', 22, 35);
  b.scatter(31, 60, ['tuft', 'rock', 'tuft'], [2, 2, GW - 3, GH - 3]);
  const mobs: MobSpawn[] = [];
  // teacup haunts and rattlebones guard the graveyards; inkveils drift about at night; the Last Candle keeps the chapel door
  for (const [gx, gy] of [[5, 8], [32, 8], [5, 24], [32, 24]] as [number, number][]) { mobs.push({ kind: 'ghoul', x: gx + 2, y: gy + 3 }, { kind: 'ghoul', x: gx + 8, y: gy + 6 }, { kind: gy === 8 ? 'boneknight' : 'ghoul', x: gx + 6, y: gy + 2 }); }
  for (const [x, y] of [[16, 15], [30, 15], [12, 31], [38, 31], [20, 28], [28, 28]] as [number, number][]) mobs.push({ kind: 'wisp', x, y });
  mobs.push({ kind: 'gravekeeper', x: 24, y: 10 });
  const out = map('grave', 'Gloomhollow', b, [23, 37]);
  out.art.mood = 'gloom';
  out.data.mounts = true; out.data.flying = true;
  return { ...out, mobs };
}

// ---------- the Rustwind Crags: high rocky country north of town (levels 10-17, party country) ----------
function crags() {
  const W = 56, H = 40, b = new ZoneBuilder(W, H, T.ROCK), r = rng(1717);
  // peaks all round the edge, a sandy trail winding north from the way in at the bottom
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if ((x < 2 || y < 2 || x > W - 3 || (y > H - 3 && Math.abs(x - 28) > 1)) && !b.blocked(x, y)) b.put('peak', x, y, Math.floor(r() * 3));
  const trailX = (y: number) => Math.round(28 + Math.sin(y * 0.22) * 6 * Math.min(1, Math.max(0, (36 - y) / 6)));   // straight out the bottom
  for (let y = 4; y < H; y++) { const x = trailX(y); b.fill(x - 1, y, x, y, T.SAND); }
  b.fill(10, 20, 46, 21, T.SAND);                                         // a cross path to the ravine and the old works
  // the Ember Ravine (west): dead trees and boulders, where the cinderbats roost
  for (const [x, y] of [[6, 22], [9, 30], [15, 24], [13, 31], [5, 27], [17, 29]] as [number, number][]) b.put('deadtree', x, y);
  // the Old Clockworks (east): a ruined workshop of arches, anvils and crates, buzzing with wasps
  b.fill(38, 23, 50, 31, T.STONE);
  b.put('anvil', 43, 27).put('anvil', 46, 29).put('crate', 39, 29).put('crate', 40, 30).put('barrel', 49, 27).put('barrel', 49, 28).put('crate', 44, 31);
  for (let x = 38; x <= 50; x++) if (x % 3 !== 0) b.put('fence_h', x, 22);
  // the frozen tarn (north), ringed with pines: Frostmaws keep it
  b.ellipse(20, 9, 7, 4, T.WATER).ellipse(20, 9, 9, 5.5, T.SAND); b.ellipse(20, 9, 7, 4, T.WATER);
  for (let i = 0; i < 26; i++) { const a = i / 26 * Math.PI * 2, x = Math.round(20 + Math.cos(a) * 11), y = Math.round(9 + Math.sin(a) * 6.5); if (b.free(x, y) && b.at(x, y) !== T.SAND) b.tree(x, y, 'pine'); }
  // Old Rootjaw's hollow (north-east): a grove of dead wood
  b.ellipse(44, 9, 6, 4, T.GRASS);   // the only green up here: it grows round Old Rootjaw
  for (const [x, y] of [[39, 6], [49, 7], [41, 12], [48, 12], [44, 5]] as [number, number][]) b.put('deadtree', x, y);
  for (let i = 0; i < 60; i++) { const x = 3 + Math.floor(r() * (W - 6)), y = 3 + Math.floor(r() * (H - 6)); if (b.at(x, y) === T.ROCK && b.free(x, y) && Math.abs(x - trailX(y)) > 2) b.put(r() < 0.65 ? 'rock' : 'pine', x, y, Math.floor(r() * 3)); }
  b.put('wayshrine', 31, 34).put('sign', 25, 36);
  b.scatter(171, 50, ['tuft', 'tuft', 'rock'], [2, 2, W - 3, H - 3], []);
  const mobs: MobSpawn[] = [];
  for (const [x, y] of [[8, 24], [12, 27], [14, 22], [7, 30], [11, 33], [16, 27], [4, 25], [18, 32]] as [number, number][]) mobs.push({ kind: 'cinderbat', x, y });
  for (const [x, y] of [[41, 26], [44, 25], [47, 27], [42, 29], [45, 30], [48, 30], [39, 27], [50, 25]] as [number, number][]) mobs.push({ kind: 'wasp', x, y });
  for (const [x, y] of [[12, 8], [28, 9], [20, 15], [14, 13], [26, 4]] as [number, number][]) mobs.push({ kind: 'frostmaw', x, y });
  mobs.push({ kind: 'rootjaw', x: 44, y: 9 });
  const out = map('crags', 'Rustwind Crags', b, [28, 37]);
  out.data.mounts = true; out.data.flying = false;   // the winds up here would throw you off
  return { ...out, mobs };
}

// ---------- the Dragon's Spine: the summit where the Star-Eater sleeps ----------
function summit() {
  const W = 40, H = 30, b = new ZoneBuilder(W, H, T.ROCK);
  const r = rng(77);
  // a ring of peaks around a wide, flat summit; a path down the back of the mountain to the Sky Dock
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = ((x - 20) / 18) ** 2 + ((y - 13) / 13) ** 2;
    if (d > 1 && !(x >= 19 && x <= 21 && y > 20)) b.put('peak', x, y, Math.floor(r() * 3));
  }
  b.fill(19, 20, 21, H - 1, T.SAND).ellipse(20, 11, 7, 4, T.SAND);
  for (let i = 0; i < 18; i++) { const x = 4 + Math.floor(r() * (W - 8)), y = 3 + Math.floor(r() * (H - 8)); if (b.at(x, y) === T.ROCK && !b.blocked(x, y) && Math.hypot(x - 20, y - 11) > 6) b.put('rock', x, y); }
  b.put('sign', 22, 24);
  const out = map('spine', "Dragon's Spine", b, [20, 26]);
  out.art.mood = 'ember';
  out.data.mounts = true; out.data.flying = false;
  return { ...out, mobs: [{ kind: 'ember_wyrm', x: 20, y: 10 }] as MobSpawn[] };
}

// ---------- a hidden grotto: no name on any map, it turns up somewhere new on each continent once it's cleared ----------
function grotto() {
  const W = 30, H = 20, b = new ZoneBuilder(W, H, T.FOREST), r = rng(4242);
  b.ellipse(15, 10, 12.5, 7.5, T.GRASS);
  b.ellipse(22, 13, 3, 2, T.WATER);
  b.fill(14, 15, 15, 19, T.PATH);
  b.border([[14, 18, 15, 19]]);
  for (let i = 0; i < 70; i++) { const x = 2 + Math.floor(r() * (W - 4)), y = 2 + Math.floor(r() * (H - 4)); if (b.at(x, y) === T.FOREST && !b.blocked(x, y)) b.tree(x, y, r() < 0.5 ? 'pine' : 'oak'); }
  for (const [x, y] of [[8, 6], [21, 6], [6, 12], [11, 14], [19, 9]] as [number, number][]) b.put('bigshroom', x, y, (x + y) % 3);
  b.put('reeds', 20, 12, 0, false).put('reeds', 24, 14, 1, false);
  b.scatter(9, 30, ['tuft', 'mushroom', 'tuft'], [3, 3, W - 4, H - 4], []);
  return map('grotto', 'A hidden grotto', b, [14, 17]);
}
/** Where a grotto's treasure is (tiles), and its way out. */
export const GROTTO_CHEST: [number, number] = [15, 5];

// ---------- the ferry's deck: the long crossing between homelands, out on the open ocean ----------
function deck() {
  const W = 34, H = 18, b = new ZoneBuilder(W, H, T.WATER);
  // the hull: a long deck of planks, pointed at the bow (east)
  for (let y = 5; y <= 12; y++) for (let x = 5; x <= 27; x++) b.fill(x, y, x, y, T.PIER);
  for (let i = 0; i < 4; i++) for (let y = 6 + i; y <= 11 - i; y++) b.fill(28 + i, y, 28 + i, y, T.PIER);
  b.fenceH(5, 27, 4).fenceH(5, 27, 13);   // the rails (and the edge of the deck)
  for (let y = 4; y <= 13; y++) b.block(4, y);
  for (let i = 0; i < 4; i++) { b.block(28 + i, 5 + i); b.block(28 + i, 12 - i); }
  b.block(32, 8, 32, 9);
  b.put('crate', 7, 6).put('crate', 8, 6).put('barrel', 7, 11).put('barrel', 25, 6).put('crate', 24, 11).put('barrel', 25, 11);
  b.put('lamp', 12, 6).put('lamp', 20, 6).put('lamp', 12, 11).put('lamp', 20, 11);
  b.bench(9, 9).bench(21, 9);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (b.at(x, y) === T.WATER) b.block(x, y);   // the sea all round
  return map('deck', 'On the ferry', b, [16, 10]);
}

const h = harbor(), a = artisan(), c = cottages(), g = gate(), m = meadow(), ow = overworld(), wd = woods(), gy = graveyard(), sp = summit(), cr = crags(), dk = deck(), gr = grotto();
export const ZONES: ZoneDef[] = [
  { id: 'town', name: 'Tiny Town Square', kind: 'town', mounts: false, flying: false, map: null as unknown as ZoneMapData, art: null, wayshrine: [17, 19], mobs: [] },
  { id: 'harbor', name: 'Harbor', kind: 'town', mounts: false, flying: false, map: h.data, art: h.art, wayshrine: [18, 7], mobs: [] },
  { id: 'artisan', name: 'Artisan Row', kind: 'town', mounts: false, flying: false, map: a.data, art: a.art, wayshrine: [20, 13], mobs: [] },
  { id: 'cottages', name: 'Cottage Lane', kind: 'town', mounts: false, flying: false, map: c.data, art: c.art, wayshrine: [3, 13], mobs: [] },
  { id: 'gate', name: 'Town Gate', kind: 'town', mounts: false, flying: false, map: g.data, art: g.art, wayshrine: [9, 5], mobs: [] },
  { id: 'meadow', name: 'Meadowlands', kind: 'field', mounts: true, flying: true, map: m.data, art: m.art, wayshrine: [33, 6], mobs: m.mobs },
  { id: 'world', name: 'The World', kind: 'world', mounts: true, flying: true, map: ow.data, art: ow.art, wayshrine: null, mobs: ow.mobs },
  { id: 'woods', name: 'Whisperwood', kind: 'field', mounts: true, flying: false, map: wd.data, art: wd.art, wayshrine: [54, 19], mobs: wd.mobs },
  { id: 'grave', name: 'Gloomhollow', kind: 'field', mounts: true, flying: true, map: gy.data, art: gy.art, wayshrine: [26, 33], mobs: gy.mobs },
  { id: 'spine', name: "Dragon's Spine", kind: 'field', mounts: true, flying: false, map: sp.data, art: sp.art, wayshrine: null, mobs: sp.mobs },
  { id: 'grotto', name: 'A hidden grotto', kind: 'field', mounts: false, flying: false, map: gr.data, art: gr.art, wayshrine: null, mobs: [] },
  { id: 'deck', name: 'On the ferry', kind: 'town', mounts: false, flying: false, map: dk.data, art: dk.art, wayshrine: null, mobs: [] },
  { id: 'crags', name: 'Rustwind Crags', kind: 'field', mounts: true, flying: false, map: cr.data, art: cr.art, wayshrine: [31, 34], mobs: cr.mobs },
];
/** Which arena a fight here happens in: the woods, the graveyard and the summit have their own; on the world map,
 *  whichever wild place is nearest (x, y in pixels); everywhere else, a meadow clearing. */
export function arenaFor(base: string, x: number, y: number): ArenaPlace {
  if (base === 'woods' || base === 'grave' || base === 'spine' || base === 'meadow' || base === 'crags') return base;
  if (base.startsWith('deck') || base.startsWith('airdeck')) return 'deck';
  if (base.startsWith('grotto')) return 'woods';   // fights at sea, on the ferry (or the airship)
  for (const n of OTHER_HOMELANDS) if (n.arenas?.[base]) return n.arenas[base]!;
  if (zoneDef(base)?.kind !== 'world') return 'meadow';
  const arenaOf = (z: string | null) => (z === 'meadow' || z === 'woods' || z === 'grave' || z === 'crags' ? z : OTHER_HOMELANDS.find(n => n.arenas?.[z ?? ''])?.arenas?.[z ?? '']) ?? null;
  const wild = PLACES.filter(p => (p.world ?? 'world') === base && arenaOf(p.zone)).map(p => ({ ...p, zone: arenaOf(p.zone) }));
  const near = wild.sort((a, b) => Math.hypot(a.tile[0] * 16 - x, a.tile[1] * 16 - y) - Math.hypot(b.tile[0] * 16 - x, b.tile[1] * 16 - y))[0];
  return (near?.zone ?? 'meadow') as ArenaPlace;
}
// the other homelands: their towns, fields and continents (shared/nation-*.ts)
for (const n of OTHER_HOMELANDS) { ZONES.push(...n.zones); PLACES.push(...n.places); }
ZONES.push(GARDEN_ZONE);   // your backyard (shared/garden.ts): a private copy behind each home
/** The overworld (continent) a zone belongs to: where its world map comes from. */
export function worldOf(zone: string): string {
  const base = zone.split(/[~#]/)[0];
  if (zoneDef(base)?.kind === 'world') return base;
  for (const n of OTHER_HOMELANDS) if (n.zones.some(z => z.id === base)) return n.zones.find(z => z.kind === 'world')?.id ?? 'world';
  return 'world';
}
export const zoneDef = (id: string): ZoneDef | undefined => ZONES.find(z => z.id === id) ?? ARENAS.find(z => z.id === id);
